<script lang="ts" setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { useAppStore } from "@/store/app.store";
import { useBaseView } from "@/composables/useBaseView";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { MapGroupPermissions } from "@/constants/permissions";
import type { SidebarMenuItem, SidebarPermission } from "@/types/sidebar";

const props = withDefaults(
  defineProps<{
    isCollapsed?: boolean;
    openThreadsCount?: number;
  }>(),
  {
    isCollapsed: true,
  },
);

const route = useRoute();
const { t } = useI18n();
const appStore = useAppStore();
const { hasAccess, getAssetUrl } = useBaseView();

const isVisible = (permission?: SidebarPermission): boolean => {
  if (permission === undefined) return true;
  if (typeof permission === "function") return permission();
  return hasAccess(permission);
};

const allMenus: SidebarMenuItem[] = [
  {
    icon: "mdi-message-outline",
    title: t("chats.chat"),
    to: { name: "chat" },
    count: props.openThreadsCount,
    permission: DispatcherPermissionValue.ViewChat,
  },
  {
    icon: "mdi-account-multiple-outline",
    title: t("users.users"),
    url: "/users",
    permission: DispatcherPermissionValue.ViewUsers,
    items: [
      { title: t("drivers.drivers"), to: { name: "drivers" } },
      { title: t("drivers.owners"), to: { name: "owners" } },
      { title: t("drivers.coordinators"), to: { name: "coordinators" } },
    ],
  },
  {
    icon: "mdi-package-variant-closed",
    title: "Trips",
    items: [
      { title: "Manage orders", to: { name: "orders" }, permission: DispatcherPermissionValue.ViewOrders },
      { title: "Trip monitor", to: { name: "trip-monitor" }, permission: DispatcherPermissionValue.ViewTripMonitor },
    ],
  },
  {
    icon: "mdi-truck-outline",
    title: "Fleet",
    url: "/fleets",
    items: [
      { title: t("trucks.trucks"), to: { name: "trucks" }, permission: DispatcherPermissionValue.ViewTrucks },
      {
        title: t("trucks.history"),
        to: { name: "trucks-history" },
        permission: DispatcherPermissionValue.ViewTrucks,
      },
      {
        title: t("trucks.search"),
        to: { name: "trucks-search" },
        permission: DispatcherPermissionValue.AccessToTruckSearch,
      },
      { title: t("trucks.map"), to: { name: "trucks-map" }, permission: MapGroupPermissions },
    ],
  },
  {
    icon: "mdi-domain",
    title: t("company.companies"),
    url: "/company",
    permission: DispatcherPermissionValue.ViewCompanies,
    items: [
      { title: t("company.carriers"), to: { name: "carriers" } },
      { title: t("company.customers"), to: { name: "customers" } },
      { title: t("company.facilities"), to: { name: "facilities" } },
    ],
  },
  {
    icon: "mdi-currency-usd",
    title: t("billing.billing"),
    url: "/billing",
    permission: DispatcherPermissionValue.AccessToBilling,
    items: [{ title: t("billing.reports"), to: { name: "billing-reports" } }],
  },
  {
    icon: "mdi-clipboard-text-outline",
    title: t("dispatchers.logs"),
    to: { name: "logs" },
    permission: () =>
      hasAccess(DispatcherPermissionValue.ViewDispatchers) && hasAccess(DispatcherPermissionValue.ViewLogs),
  },
  {
    icon: "mdi-account-circle-outline",
    title: t("dispatchers.dispatchers"),
    to: { name: "dispatchers" },
    permission: DispatcherPermissionValue.ViewDispatchers,
  },
];

const menu = computed<SidebarMenuItem[]>(() => {
  return allMenus
    .filter((item) => isVisible(item.permission))
    .map((item) => (item.items ? { ...item, items: item.items.filter((child) => isVisible(child.permission)) } : item))
    .filter((item) => !item.items || item.items.length > 0);
});

const hasActiveChild = (item: SidebarMenuItem) => {
  return item.items?.some((child) => child.to.name === route.name) ?? false;
};
</script>

<template>
  <v-navigation-drawer id="main-sidebar" :permanent="true" app class="pt-3 pb-8 dark-background" width="250">
    <div class="sidebar-navigation d-flex flex-column justify-sm-space-between height-100">
      <div>
        <!-- Sidebar header -->
        <div
          :class="{ 'justify-center': isCollapsed }"
          class="sidebar-nav__header pt-3 pb-3 mb-9 d-flex px-4 align-center justify-center"
        >
          <img
            v-if="appStore.dispatcher?.company?.code"
            :src="getAssetUrl('logo/' + appStore.dispatcher.company.code + '.svg')"
            alt="Logo"
            class="company-logo"
          />
          <span v-if="!isCollapsed" class="company-name ml-2">{{ appStore.dispatcher?.company.name }}</span>
        </div>
        <div
          :class="{ 'pl-4': !isCollapsed, 'text-center': isCollapsed }"
          class="text-uppercase caption font-weight-600 text-nav"
        >
          Menu
        </div>

        <!-- Sidebar navigation -->
        <v-list density="compact">
          <template v-for="(item, i) in menu">
            <!-- Collapsed sidebar navigation -->
            <v-menu
              v-if="item.items && isCollapsed"
              :key="'menu-item-' + i"
              content-class="sidebar-submenu"
              location="right"
              tile
              transition="slide-x-transition"
            >
              <template #activator="{ props: menuProps }">
                <v-list-item :class="{ 'v-list-item--active': hasActiveChild(item) }" v-bind="menuProps">
                  <template v-if="item.icon" #prepend>
                    <v-icon :color="item.color" size="20">
                      {{ item.icon }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="pa-0">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list>
                <v-list-item v-for="(child, j) in item.items" :key="j" :link="true" :to="child.to">
                  <v-list-item-title class="pa-0">
                    {{ child.title }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <!-- Full sidebar navigation -->
            <v-list-group v-else-if="item.items && !isCollapsed" :key="i" v-model="item.active" :group="item.url">
              <template #activator="{ props: groupProps }">
                <v-list-item v-bind="groupProps">
                  <template v-if="item.icon" #prepend>
                    <v-icon :color="item.color" size="20">
                      {{ item.icon }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="pa-0">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>

              <v-list-item v-for="(child, m) in item.items" :key="m" :link="true" :to="child.to">
                <v-list-item-title class="pa-0">
                  {{ child.title }}
                </v-list-item-title>
              </v-list-item>
            </v-list-group>
            <v-list-item v-else :key="item.title" :link="true" :to="item.to">
              <template v-if="item.icon" #prepend>
                <v-icon :color="item.color" size="20">
                  {{ item.icon }}
                </v-icon>
              </template>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </div>
      <div v-if="!isCollapsed" class="pl-5 pr-5 text-center text-body-2 mt-4 text-nav">&copy; Omni Dispatch, 2023</div>
    </div>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.sidebar-navigation {
  .company-logo {
    width: 26px;
    height: 26px;
  }

  .company-name {
    font-size: 16px;
    font-weight: 500;
    color: $white;
  }

  .caption {
    font-size: 11px;
  }

  .v-list-item {
    .v-list-item-title {
      color: $nav;
    }

    .v-icon {
      color: $nav;
    }

    :deep(.v-list-item__append) {
      .v-icon {
        color: $nav;
      }
    }

    :deep(.v-list-item__prepend) {
      display: block;
      text-align: center;
    }

    &--active {
      background-color: $dark-nav;

      &:before {
        content: "";
        border-left: 3px solid $primary;
        position: absolute;
        height: 100%;
      }

      .v-list-item-title {
        color: $white;
      }

      .v-icon {
        color: $white;
      }

      :deep(.v-list-item__append) {
        .v-icon {
          color: $white;
        }
      }
    }
  }

  .v-list-group--open {
    .v-list-group__items {
      .v-list-item--active {
        &:before {
          border-left: none;
        }
      }
    }
  }

  .v-list-group__items {
    .v-list-item {
      padding-inline-start: 52px !important;
    }
  }
}
</style>
